import { apiClient } from "@/lib/api/http"

export interface CompanySearchResult {
  id: string
  name: string
  legalName?: string
  description?: string
  companyType?:string
  tags?: string[]
  email?: string
  phoneNumber?: string
  website?: string
  logoUrl?: string | null
  taxId?: string | null
  addressLine1?: string
  addressLine2?: string | null
  city?: string
  state?: string
  postalCode?: string
  country?: string
  verificationStatus?: string
  yearEstablished?: number | null
  annualRevenue?: string | null
  createdAt?: string
  updatedAt?: string
  rank?: number
}

export interface ProductSearchResult {
  id: string
  productId: string
  variantId: string
  companyId: string
  name?:string
  productName: string
  companyName?: string
  company?: { id?: string; name?: string; website?: string }
  description?: string
  tags?: string[]
  productCode?: string
  productCategory?: string
  productSubCategory?: string
  imageUrl?: string | null
  imageUrls?: { url: string; caption?: string | null; order?: number }[]
  price?: number
  currencyCode?: string
  createdAt?: string
  updatedAt?: string
  rank?: number
}

export interface ExternalProductSearchResult {
  id: string
  externalSellerId?: string
  productName: string
  productUrl?: string
  productImageUrl?: string | null
  companyName?: string
  companyUrl?: string
  rank?: number
}

export interface ExternalSeller {
  id?: string
  productName?: string
  price?: string
  sellerName: string
  location?: string
  rating?: string
  reviewsCount?: string
}

export interface LeadSeller {
  id?: string
  name: string
  website?: string | null
  addressLine1?: string | null
  city?: string | null
  state?: string | null
  companyType?: string | null
  description?: string | null
  yearEstablished?: number | null
  annualRevenue?: string | null
  isExternal?: boolean
}

export interface SearchResponseData {
  companies: CompanySearchResult[]
  products: ProductSearchResult[]
  externalProducts?: ExternalProductSearchResult[]
  externalSellers?: ExternalSeller[]
  leadSellers?: LeadSeller[]
  externalSearchId?: string
}

interface Envelope<T> {
  success: boolean
  statusCode: number
  message: string
  data: T
}

export interface CompanyFilters {
  exportMarkets?: string[]
  certifications?: string[]
  industry?: string[]
  companyType?: string[]
  location?: string[]
  natureOfBusiness?: string[]
  awards?: string[]
  memberships?: string[]
  shippingMethods?: string[]
  paymentTerms?: string[]
  acceptedDeliveryTerms?: string[]
  acceptedPaymentCurrency?: string[]
  acceptedPaymentMethods?: string[]
  infrastructure?: string[]
  importMarkets?: string[]
  legalStatus?: string[]
}

export interface ProductFilters {
  minPrice?: number
  maxPrice?: number
  category?: string[]
  minMoq?: number
}

export async function searchSuppliers(
  query: string,
  token?: string,
  companyFilters?: CompanyFilters,
  productFilters?: ProductFilters
): Promise<SearchResponseData> {
  const params: Record<string, any> = {
    query: query,
  }

  if (companyFilters) {
    if (companyFilters.exportMarkets?.length) params['companyFilters[exportMarkets]'] = companyFilters.exportMarkets.join(',')
    if (companyFilters.certifications?.length) params['companyFilters[certifications]'] = companyFilters.certifications.join(',')
    if (companyFilters.industry?.length) params['companyFilters[industry]'] = companyFilters.industry.join(',')
    if (companyFilters.companyType?.length) params['companyFilters[companyType]'] = companyFilters.companyType.join(',')
    if (companyFilters.location?.length) params['companyFilters[location]'] = companyFilters.location.join(',')
    if (companyFilters.natureOfBusiness?.length) params['companyFilters[natureOfBusiness]'] = companyFilters.natureOfBusiness.join(',')
    if (companyFilters.awards?.length) params['companyFilters[awards]'] = companyFilters.awards.join(',')
    if (companyFilters.memberships?.length) params['companyFilters[memberships]'] = companyFilters.memberships.join(',')
    if (companyFilters.shippingMethods?.length) params['companyFilters[shippingMethods]'] = companyFilters.shippingMethods.join(',')
    if (companyFilters.paymentTerms?.length) params['companyFilters[paymentTerms]'] = companyFilters.paymentTerms.join(',')
    if (companyFilters.acceptedDeliveryTerms?.length) params['companyFilters[acceptedDeliveryTerms]'] = companyFilters.acceptedDeliveryTerms.join(',')
    if (companyFilters.acceptedPaymentCurrency?.length) params['companyFilters[acceptedPaymentCurrency]'] = companyFilters.acceptedPaymentCurrency.join(',')
    if (companyFilters.acceptedPaymentMethods?.length) params['companyFilters[acceptedPaymentMethods]'] = companyFilters.acceptedPaymentMethods.join(',')
    if (companyFilters.infrastructure?.length) params['companyFilters[infrastructure]'] = companyFilters.infrastructure.join(',')
    if (companyFilters.importMarkets?.length) params['companyFilters[importMarkets]'] = companyFilters.importMarkets.join(',')
    if (companyFilters.legalStatus?.length) params['companyFilters[legalStatus]'] = companyFilters.legalStatus.join(',')
  }

  if (productFilters) {
    if (productFilters.category?.length) params['productFilters[category]'] = productFilters.category.join(',')
    if (productFilters.minPrice !== undefined) params['productFilters[minPrice]'] = productFilters.minPrice
    if (productFilters.maxPrice !== undefined) params['productFilters[maxPrice]'] = productFilters.maxPrice
    if (productFilters.minMoq !== undefined) params['productFilters[minMoq]'] = productFilters.minMoq
  }

  const now = new Date()
  console.log(`📡 [HTTP GET /search] Dispatching API Call for "${query}" | Timestamp: ${now.toISOString()} (${now.toLocaleTimeString()})`)

  const headers: Record<string, string> = {}
  if (token) headers["Authorization"] = `Bearer ${token}`

  const { data: envelope } = await apiClient.get<Envelope<SearchResponseData>>(
    "/search",
    {
      params,
      paramsSerializer: (params) => {
        const urlParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined) {
            urlParams.append(key, String(value));
          }
        });
        return urlParams.toString().replace(/%5B/g, '[').replace(/%5D/g, ']');
      },
      headers,
      withCredentials: true,
    }
  )
  return envelope.data
}



export interface CompanyFiltersResponse {
  exportMarkets?: string[]
  certifications?: string[]
  natureOfBusiness?: string[]
  awards?: string[]
  memberships?: string[]
  shippingMethods?: string[]
  paymentTerms?: string[]
  acceptedDeliveryTerms?: string[]
  acceptedPaymentCurrency?: string[]
  acceptedPaymentMethods?: string[]
  infrastructure?: string[]
  importMarkets?: string[]
  legalStatus?: string[]
  industry?: string[]
  companyType?: string[]
  location?: string[]
}

export interface ProductFiltersResponse {
  category?: string[]
  minPrice?: string | number
  maxPrice?: string | number
  minMoq?: string | number
}

export interface SearchFiltersData {
  companyFilters: CompanyFiltersResponse
  productFilters: ProductFiltersResponse
}

export async function getSearchFilters(token?: string): Promise<SearchFiltersData> {
  const headers: Record<string, string> = {}
  if (token) headers["Authorization"] = `Bearer ${token}`

  const { data: envelope } = await apiClient.get<Envelope<SearchFiltersData>>(
    "/search/filters",
    { headers }
  )
  return envelope.data
}

export interface ExternalSellerDetail {
  id: string
  name: string
  website?: string | null
  addressLine1?: string | null
  city?: string | null
  state?: string | null
  companyType?: string | null
  description?: string | null
  yearEstablished?: number | null
  annualRevenue?: string | null
  isExternal: boolean
}

export async function getExternalSeller(
  id: string,
  token?: string
): Promise<ExternalSellerDetail> {
  const headers: Record<string, string> = {}
  if (token) headers["Authorization"] = `Bearer ${token}`

  const { data: envelope } = await apiClient.get<Envelope<ExternalSellerDetail>>(
    `/external-sellers/${id}`,
    { headers }
  )
  return envelope.data
}



